import { useCallback, useMemo } from 'react';

import { type RadarPrediction } from '@garnish/location';
import { useSelector } from '@xstate/react';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { type LocationSearchService } from '../../machine';
import { getRadarPredictionId } from '../../machine/utils';

// ─────────────────────────────────────────────────────────────────────────────

export const usePredictions = (params: Params) => {
  const { locationSearchActorRef } = params;

  // ─── Derived Data ──────────────────────────────────────────────────────────

  const locationSearchType = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.locationSearchType,
  );

  const predictions = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.predictions ?? [],
  );

  const predictionQuery = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.predictionsSearchQuery ?? '',
  );

  const predictionLocations = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.predictionLocations,
  );

  const formattedPredictions = useMemo(() => predictions.map(formatPrediction), [predictions]);

  // ─── Flags ───────────────────────────────────────────────────────────────────

  const isSurfaceDistanceForPredictionEnabled = useFeatureFlag('CELS-4064-surface-prediction-distance-on-location-cards-enabled'); // prettier-ignore

  const isFetchingPredictions = useSelector(
    locationSearchActorRef,
    (snapshot) =>
      snapshot.matches({ searchForPredictions: 'searching' }) ||
      snapshot.matches({ searchForPredictions: 'selecting' }) ||
      (snapshot.matches({ searchForPredictions: 'debouncing' }) &&
        snapshot.context.predictions.length === 0),
  );

  const pickupLocations = predictionLocations?.pickup ?? [];
  const outpostLocations = predictionLocations?.outpost ?? [];

  const filteredPredictionLocations =
    locationSearchType === 'outpost' ? outpostLocations : pickupLocations;

  const hasPredictions = formattedPredictions.length > 0 || filteredPredictionLocations.length > 0;

  const hasNoPredictions = Boolean(
    !isFetchingPredictions && !hasPredictions && predictionLocations && predictions,
  );

  const shouldRenderPredictions = hasPredictions || hasNoPredictions || predictionQuery.length > 0;

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const selectPrediction = useCallback(
    (predictionId: string) => {
      locationSearchActorRef.send({
        type: 'SELECT_PREDICTION',
        predictionId,
      });
    },
    [locationSearchActorRef],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isSurfaceDistanceForPredictionEnabled,
    isFetchingPredictions,
    formattedPredictions,
    predictionLocations: filteredPredictionLocations,
    predictionQuery,
    hasPredictions,
    hasNoPredictions,
    shouldRenderPredictions,
    selectPrediction,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrediction(prediction: RadarPrediction): FormattedPrediction {
  return {
    id: getRadarPredictionId(prediction),
    address: prediction.formattedAddress,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  locationSearchActorRef: LocationSearchService;
};

type FormattedPrediction = {
  id: string;
  address: string;
  name?: string;
};
